import { useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  computeTotals,
  formatGBP,
  type BookingInput,
  type StepId,
} from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { Plate } from "../../components/Plate";
import { TrustStrip } from "../../components/TrustStrip";
import { api, ApiError } from "../../api/client";
import { sendBookingComplete } from "../../iframe/bridge";
import { track } from "../../analytics/analytics";
import { resolveVehicle, useSession } from "../../store/sessionStore";
import { formatLongDate } from "../dates";
import type { StepProps } from "../stepTypes";
import flow from "../BookingFlow.module.css";
import s from "../steps.module.css";

/** Step 8 — review everything with Change links, then confirm the booking. */
export function SummaryStep({ onNext, goTo }: StepProps) {
  const state = useSession();
  const config = state.config!;
  const services = config.sandbox ? config.services : state.availableServices;
  const selectedServices = useMemo(
    () => services.filter((sv) => state.selectedServiceIds.includes(sv.id)),
    [services, state.selectedServiceIds],
  );
  const totals = computeTotals(services, state.selectedServiceIds);
  const branch =
    (state.branches.length ? state.branches : config.branches).find(
      (b) => b.id === state.branchId,
    ) ?? null;
  const canChangeLocation =
    (state.branches.length ? state.branches : config.branches).length > 1 &&
    config.flow.steps.includes("location");
  const vehicle = resolveVehicle(state);
  const reg =
    (vehicle && "registration" in vehicle && vehicle.registration) ||
    state.vehicleRegistration ||
    null;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Change links edit a single step, then bounce straight back to the summary
  // instead of re-walking the whole flow.
  const steps = config.flow.steps;
  const summaryIndex = steps.indexOf("summary");
  const change = (step: StepId) => () => {
    const idx = steps.indexOf(step);
    if (idx < 0) return;
    if (summaryIndex >= 0) state.goToForEdit(idx, summaryIndex);
    else goTo(step);
  };

  async function confirm() {
    if (submitting) return;
    setError(null);

    if (!vehicle || !branch || !state.slot || !state.contact.name) {
      setError(
        "Some details are missing — please use the Change links to complete them.",
      );
      return;
    }
    const requiredContactFields = new Set(
      state.contactInfoFields?.required ?? [],
    );
    if (
      (requiredContactFields.has("contact_number") && !state.contact.phone) ||
      (requiredContactFields.has("contact_address") && !state.contactAddress) ||
      (requiredContactFields.has("contact_postcode") &&
        !(state.contactPostcode ?? state.postcode))
    ) {
      setError(
        "The garage requires more contact details — please use Change to complete them.",
      );
      return;
    }

    const body: BookingInput = {
      garageId: config.id,
      branchId: branch.id,
      sessionId: state.sessionId,
      vehicle,
      vehicleRegistration: reg ?? undefined,
      contact: {
        name: state.contact.name,
        email: state.contact.email,
        phone: state.contact.phone,
      },
      serviceIds: state.selectedServiceIds,
      slotId: state.slot.id,
      slotStart: state.slot.start,
      contactAddress: state.contactAddress ?? undefined,
      contactCity: state.contactCity ?? undefined,
      contactPostcode: state.contactPostcode ?? state.postcode ?? undefined,
      notes: state.notes ?? undefined,
    };

    setSubmitting(true);
    try {
      const booking = await api.createBooking(body);
      // mark lead converted (best-effort)
      if (state.leadId) {
        void api
          .updateLead(state.leadId, { status: "converted" })
          .catch(() => {});
      }
      state.setBookingReference(booking.reference);
      state.setBookingTotalPrice(booking.totalPrice);
      track("booking_completed", {
        reference: booking.reference,
        total: booking.totalPrice,
        branchId: branch.id,
        serviceIds: state.selectedServiceIds,
      });
      sendBookingComplete(booking.reference, config.id);
      onNext(); // -> confirmation
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "We couldn't confirm that booking. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <StepHeader
        kicker="Final check"
        title={config.copy.summaryTitle}
        subtitle="Everything look right? You can change anything before confirming."
        display={false}
      />

      <div className={s.stack}>
        <div className={s.summaryGroup}>
          {/* Vehicle */}
          <div className={s.summaryRow}>
            <span className={s.summaryLabel}>Vehicle</span>
            <div className={s.summaryValue}>
              <strong>
                {vehicle
                  ? `${(vehicle as { make: string }).make} ${(vehicle as { model: string }).model}`
                  : "—"}
              </strong>
              {reg && (
                <div style={{ marginTop: 6 }}>
                  <Plate registration={reg} size="sm" />
                </div>
              )}
            </div>
            <button
              className={s.summaryChange}
              onClick={change("registration")}
            >
              Change
            </button>
          </div>

          {/* Services */}
          <div className={s.summaryRow}>
            <span className={s.summaryLabel}>Services</span>
            <div className={s.summaryValue}>
              {selectedServices.map((sv) => (
                <div key={sv.id} className={s.summaryServiceLine}>
                  <span>{sv.name}</span>
                  <span style={{ color: "var(--ap-text-soft)" }}>
                    {sv.price === 0 ? "Free" : formatGBP(sv.price)}
                  </span>
                </div>
              ))}
            </div>
            <button className={s.summaryChange} onClick={change("services")}>
              Change
            </button>
          </div>

          {/* Location */}
          <div className={s.summaryRow}>
            <span className={s.summaryLabel}>Location</span>
            <div className={s.summaryValue}>
              <strong>{branch?.name ?? "—"}</strong>
              {branch && (
                <div
                  style={{
                    color: "var(--ap-text-soft)",
                    fontSize: 13,
                    marginTop: 2,
                  }}
                >
                  {branch.addressLine}, {branch.postcode}
                </div>
              )}
            </div>
            {canChangeLocation && (
              <button className={s.summaryChange} onClick={change("location")}>
                Change
              </button>
            )}
          </div>

          {/* Date & time */}
          <div className={s.summaryRow}>
            <span className={s.summaryLabel}>When</span>
            <div className={s.summaryValue}>
              {state.slot ? (
                <>
                  <strong>
                    {formatLongDate(state.slot.start.slice(0, 10))}
                  </strong>
                  <div
                    style={{
                      color: "var(--ap-text-soft)",
                      fontSize: 13,
                      marginTop: 2,
                    }}
                  >
                    {state.slot.label}
                  </div>
                </>
              ) : (
                "—"
              )}
            </div>
            <button className={s.summaryChange} onClick={change("datetime")}>
              Change
            </button>
          </div>

          {/* Contact */}
          <div className={s.summaryRow}>
            <span className={s.summaryLabel}>Contact</span>
            <div className={s.summaryValue}>
              <strong>{state.contact.name ?? "—"}</strong>
              <div
                style={{
                  color: "var(--ap-text-soft)",
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                {[state.contact.email, state.contact.phone]
                  .filter(Boolean)
                  .join(" · ")}
                {state.contactAddress && (
                  <div>
                    {[
                      state.contactAddress,
                      state.contactCity,
                      state.contactPostcode,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}
              </div>
            </div>
            <button className={s.summaryChange} onClick={change("details")}>
              Change
            </button>
          </div>
        </div>

        {/* Total */}
        <div className={s.totalCard}>
          <div className={s.totalRow}>
            <span className={s.totalLabel}>Total to pay at the branch</span>
            <span className={s.totalValue}>{formatGBP(totals.totalPrice)}</span>
          </div>
          <span className={s.totalVat}>No payment taken now</span>
        </div>

        {error && (
          <p
            role="alert"
            style={{
              color: "var(--ap-danger)",
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            {error}
          </p>
        )}
      </div>

      <div className={s.actions}>
        <Button
          block
          loading={submitting}
          onClick={confirm}
          leadingIcon={<CheckCircle2 size={18} />}
        >
          {config.copy.ctaConfirm}
        </Button>
      </div>

      {config.trust.enabled && (
        <div className={flow.trustWrap} style={{ marginTop: 18 }}>
          <TrustStrip trust={config.trust} />
        </div>
      )}
    </div>
  );
}
